// src/types/jump.ts
/**
 * Phase 2: Jump Indicator Layout Fix - Type Definitions
 * 
 * Enhanced Jump interface with:
 * - Line assignment (primary/secondary)
 * - Chain detection support
 * - Layout settings
 * 
 * Based on DNL Enhancement Specification v1.0, Section 3.3
 */

// =============================================================================
// CORE JUMP INTERFACE
// =============================================================================

/**
 * Jump indicator representing an operation on the number line
 * Enhanced with line assignment for DNL support
 */
export interface Jump {
  /** Unique identifier */
  id: string;
  /** Starting value on the number line */
  startValue: number;
  /** Ending value on the number line */
  endValue: number;
  /** Optional custom label (overrides auto-generated label) */
  label?: string;
  /** Whether to auto-generate label from span */
  autoLabel: boolean;
  /** Visual color for the jump */
  color: string;
  /** Direction of the jump */
  direction: 'positive' | 'negative';
  /** Whether the jump is currently selected */
  selected: boolean;
  /** Computed lane for vertical stacking (set by layout algorithm) */
  lane?: number;
  
  // NEW Phase 2 properties
  /** Which line this jump belongs to */
  line: 'primary' | 'secondary';
  /** Whether this jump should chain horizontally with the previous jump */
  chainWithPrevious?: boolean;
  /** Whether the label is visible (default: true) */
  labelVisible?: boolean;
}

// =============================================================================
// JUMP CHAIN TYPES
// =============================================================================

/**
 * Represents a chain of sequential jumps
 * Jumps in a chain are rendered side-by-side at the same vertical level
 */
export interface JumpChain {
  /** Unique identifier for the chain */
  id: string;
  /** Jumps in this chain, in order */
  jumps: Jump[];
  /** Shared lane for all jumps in the chain */
  lane: number;
  /** Starting pixel position of the chain */
  startPx: number;
  /** Ending pixel position of the chain */
  endPx: number;
}

/**
 * Result of jump layout calculation
 * Contains both individual jumps with lanes and identified chains
 */
export interface JumpLayoutResult {
  /** All jumps with lane assignments */
  jumpsWithLanes: (Jump & { lane: number })[];
  /** Identified chains of sequential jumps */
  chains: JumpChain[];
  /** Maximum lane used (for calculating SVG height) */
  maxLane: number;
}

// =============================================================================
// LAYOUT SETTINGS
// =============================================================================

/**
 * Configuration for jump indicator layout
 * Based on DNL Enhancement Specification Section 3.3.3
 */
export interface JumpLayoutSettings {
  /** 
   * When true, sequential jumps (end of one = start of next) 
   * are rendered side-by-side horizontally instead of stacking vertically
   */
  chainSequentialJumps: boolean;
  /** Default arc height for jump curves in pixels */
  defaultArcHeight: number;
  /** Position of jump labels relative to the arc */
  labelPosition: 'inside' | 'above';
  /** Minimum pixel separation between non-chained jumps in the same lane */
  minLaneSeparation: number;
  /** Vertical offset per lane in pixels */
  laneOffset: number;
  /** Tolerance for detecting sequential jumps (values within this are considered equal) */
  chainTolerance: number;
  /** Jump relationship type: additive or multiplicative */
  jumpType: 'additive' | 'multiplicative';
  /** Format for scale factor display in multiplicative mode */
  scaleFactorFormat: 'SF=' | '×';
  /** Whether to show the calculation (e.g., "0+3" or "×3") */
  showCalculation: boolean;
  /** Whether to show the result value */
  showResult: boolean;
  /** Whether to show the jump value (e.g., "+3" for additive, "SF=4" or "×4" for multiplicative) */
  showJumpValue: boolean;
  /** Whether to always show signs (+ or -) on numbers */
  alwaysShowSigns: boolean;
}

/**
 * Default layout settings
 */
export const DEFAULT_JUMP_LAYOUT_SETTINGS: JumpLayoutSettings = {
  chainSequentialJumps: true, // Default ON per spec
  defaultArcHeight: 30,
  labelPosition: 'above',
  minLaneSeparation: 40,
  laneOffset: 25,
  chainTolerance: 0.001,
  jumpType: 'additive',
  scaleFactorFormat: '×',
  showCalculation: true,
  showResult: true,
  showJumpValue: true,
  alwaysShowSigns: true,
};

// =============================================================================
// JUMP INDICATORS STATE
// =============================================================================

/**
 * Redux state for jump indicators
 * Enhanced with layout settings
 */
export interface JumpIndicatorsState {
  /** All jump indicators */
  jumps: Jump[];
  /** Counter for generating unique IDs */
  nextId: number;
  /** Layout configuration */
  layoutSettings: JumpLayoutSettings;
}

/**
 * Initial state for jump indicators
 */
export const INITIAL_JUMP_INDICATORS_STATE: JumpIndicatorsState = {
  jumps: [],
  nextId: 1,
  layoutSettings: DEFAULT_JUMP_LAYOUT_SETTINGS,
};

// =============================================================================
// ACTION PAYLOADS
// =============================================================================

/**
 * Payload for adding a new jump
 */
export interface AddJumpPayload {
  startValue: number;
  endValue: number;
  /** Which line to add the jump to (defaults to 'primary') */
  line?: 'primary' | 'secondary';
  /** Optional custom label */
  label?: string;
}

/**
 * Payload for updating jump endpoints
 */
export interface UpdateJumpEndpointsPayload {
  id: string;
  startValue: number;
  endValue: number;
}

/**
 * Payload for changing jump line assignment
 */
export interface SetJumpLinePayload {
  id: string;
  line: 'primary' | 'secondary';
}

/**
 * Payload for updating layout settings
 */
export interface UpdateLayoutSettingsPayload {
  chainSequentialJumps?: boolean;
  defaultArcHeight?: number;
  labelPosition?: 'inside' | 'above';
  minLaneSeparation?: number;
  laneOffset?: number;
  chainTolerance?: number;
  jumpType?: 'additive' | 'multiplicative';
  scaleFactorFormat?: 'SF=' | '×';
  showCalculation?: boolean;
  showResult?: boolean;
  showJumpValue?: boolean;
  alwaysShowSigns?: boolean;
}
